import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import App from './App.jsx';
import SignInGate from './components/SignIn.jsx';
import { DeleteAccount, Privacy, Terms } from './pages/Legal.jsx';
import Manual from './pages/Manual.jsx';
import { hydrateTheme } from './lib/theme.js';
import { captureReferral } from './lib/referral.js';
import { captureClient } from './lib/client.js';
import './styles.css';

// index.html has already stamped the theme onto <html>; this picks up the two
// jobs it deliberately leaves undone -- requesting the web fonts that theme
// needs, and tinting the browser chrome to match. Outside React because it is
// true of the public pages as much as the app.
hydrateTheme();

// Before anything routes, and before the sign-in redirect that is about to
// replace this URL. A referral code arrives in the address bar for the first
// instant of the first page load and is gone by the second, while the screen
// that can use it is several minutes and one redirect away.
captureReferral();

// Beside it, and for the same reason: the Play app announces itself in the
// referrer of the first navigation and in a start_url query, both of which are
// gone a moment later. What the answer is used for is the two places a purchase
// can start -- the plan card in Settings and the offer when the scan quota
// bites -- because nothing is for sale inside the Play app.
captureClient();

// Only in a real build. Under `vite dev` the module graph changes on every
// keystroke, and a worker caching it would serve yesterday's component and look
// like a bug in the code that was just written.
//
// After `load` rather than immediately: registration competes with the app's
// own first requests otherwise, and the worker exists for the *next* launch, so
// winning that race would cost the current one for nothing.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Silent. A browser with workers disabled, a private window, or an insecure
    // origin refuses here, and every one of those is a browser the app works
    // perfectly well in -- there is nothing to tell the person and nothing for
    // them to do about it.
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// Inside the router, because a sign-in redirect returns to the page it started
// from and the app has to be able to route there once the session lands.
//
// Privacy and terms sit outside the gate: the people who most need them have no
// account and will not make one to read them -- someone deciding whether to
// sign up, an app store reviewer, an affiliate network checking the domain.
// Everything else is the app, behind the gate, which renders nothing when the
// server reports that auth is not required.
createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        {/* Public for the same reason: somebody deciding whether to sign up, and
            an app store reviewer, both want to read what it does first. */}
        <Route path="/help" element={<Manual />} />
        {/* Play requires this one to be reachable by somebody with no app and no
            account -- the person it is for has already uninstalled, so a page
            behind the gate would be a page they cannot open. */}
        <Route path="/account/delete" element={<DeleteAccount />} />
        <Route
          path="*"
          element={(
            <SignInGate>
              <App />
            </SignInGate>
          )}
        />
      </Routes>
    </BrowserRouter>
  </StrictMode>,
);
